import React, { useState } from "react";
import { InputAdornment, TextField, Button, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import logo from "@/assets/images/logo.png";
import styles from "./LoginForm.module.scss";

type PasswordScreenProps = {
  email: string;
  hasLoggedIn: boolean;
};

type FormValues = {
  password: string;
  confirmPassword?: string;
};

const schema = yup.object().shape({
  password: yup.string().required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match"),
});

const PasswordScreen = ({ email, hasLoggedIn }: PasswordScreenProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });



  const onSubmit = async (data: FormValues) => {
    const auth = getAuth();
    try {
      clearErrors("password");
      if (hasLoggedIn) {
        await signInWithEmailAndPassword(auth, email, data.password);

        console.log("User logged in successfully!");
        navigate("/");
      } else {
        await createUserWithEmailAndPassword(auth, email, data.password);


        console.log("User registered successfully!");
        navigate("/");
      }
    } catch (error: any) {
      console.error("Error logging in user:", error);
      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential"
      ) {
        setError("password", {
          type: "manual",
          message: "Incorrect password, please try again",
        });
      } else {
        setError("password", {
          type: "manual",
          message: error.message,
        });
      }
    }
  };

  const handleBack = () => {
    navigate(0);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <img className={styles.logoImg} src={logo} alt="kid nation logo" />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.inputs}>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  required
                  error={!!errors.password}
                  helperText={errors.password ? errors.password.message : ""}
                  sx={{
                    marginBottom: "10px",
                    width: "350px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#eff1f999",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            {!hasLoggedIn && (
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    variant="outlined"
                    required
                    error={!!errors.confirmPassword}
                    helperText={
                      errors.confirmPassword
                        ? errors.confirmPassword.message
                        : ""
                    }
                    sx={{
                      width: "350px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#eff1f999",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={handleClickShowConfirmPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            )}
          </div>

          <div className={styles.btns}>
            <Button
              variant="contained"
              style={{
                backgroundColor: "#fff",
                color: "#000",
                height: "58px",
                width: "150px",
                borderRadius: "12px",
                textTransform: "capitalize",
              }}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              style={{
                backgroundColor: "#000",
                color: "#fff",
                height: "58px",
                width: "150px",
                borderRadius: "12px",
                textTransform: "capitalize",
              }}
            >
              {hasLoggedIn ? "Log In" : "Register"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordScreen;

